import { compareOrder, nextSprint, projectProgress, sprintKeyOf, todayYmd } from '@nedelnik/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { createTaskActions } from '../core/actions.js';
import { getRuntime } from '../app/runtime.js';
import {
  checklistByTask,
  linkRecordsOf,
  linksOf,
  selectChecklistMap,
  selectLinkMap,
  selectSettings,
  selectTagMap,
  selectTaskMap,
  allTagRecordsByTask,
  tagRecordsByTask,
  tagsByTask,
  tasksOf,
  trashedOf,
  useAppStore,
} from '../state/store.js';

import { Rejected } from './Rejected.js';
import { SyncStatus } from './SyncStatus.js';
import { UndoBar } from './UndoBar.js';
import { AuthorizeScreen, parseAuthorizeRequest } from './auth/AuthorizeScreen.js';
import { LoginScreen } from './auth/LoginScreen.js';
import { Board } from './board/Board.js';
import { TaskList } from './list/TaskList.js';
import { ForwardToMoved, MovedScreen, useMovedTarget } from './moved/MovedScreen.js';
import { TipCard, useOnboardingTips } from './onboarding/Tips.js';
import { ProjectList } from './project/ProjectList.js';
import { ProjectView } from './project/ProjectView.js';
import { childrenOf, projectSummaries } from './project/projects.js';
import { QuickAdd } from './quick-add/QuickAdd.js';
import { FilterNotice, SearchBar } from './search/SearchBar.js';
import { EMPTY_FILTER, applyFilter, isFiltering } from './search/search.js';
import { SettingsView } from './settings/SettingsView.js';
import { availableSprintType } from './settings/availability.js';
import { SprintHeader } from './sprint/SprintHeader.js';
import { carryableTasks, tasksInPeriod } from './sprint/counts.js';
import { moveTargetPhrase, periodIn, periodTitle } from './sprint/labels.js';
import { pageTarget, usePageSwipe } from './sprint/page-swipe.js';
import { TagBar } from './tags/TagBar.js';
import { tagCounts } from './tags/tags.js';
import { TaskPanel } from './task/TaskPanel.js';
import { blockedIds } from './task/links.js';
import { TrashView } from './trash/TrashView.js';
import { useMousePointer, useNarrowScreen } from './view-mode.js';

import type { CreateOutcome } from '../core/actions.js';
import type { Runtime } from '../app/runtime.js';
import type { QuarantinedMutation } from '../core/db/schema.js';
import type { UndoNotice } from './UndoBar.js';
import type { Filter } from './search/search.js';
import type { TaskPanelActions } from './task/TaskPanel.js';
import type { Placement } from './task/landed.js';
import type { TaskExtras, TaskHandlers } from './task/TaskParts.js';
import type { Period, ProjectProgress, Task } from '@nedelnik/shared';

/**
 * Что показано вместо спринта.
 *
 * Список проектов и просмотр проекта живут вне спринта намеренно: задачи
 * одного проекта лежат в разных спринтах и во Входящих (3.8.4), и срез одной
 * недели на этих экранах означал бы ровно противоположное (3.8.8).
 */
type Screen =
  | { kind: 'sprint' }
  | { kind: 'projects' }
  | { kind: 'project'; id: string }
  | { kind: 'trash' }
  | { kind: 'settings' };

/**
 * Приложение: спринт, быстрая запись и два формата показа одних и тех же задач.
 *
 * Всё, что видно на экране, считается из локальной копии — сеть нужна только
 * для обмена изменениями (раздел 3.1 архитектуры). Поэтому здесь нет ни
 * загрузок, ни ожидания ответа: правка применяется сразу, а доехала она или нет,
 * говорит индикатор синхронизации.
 */
export function App(): React.ReactElement {
  const [runtime, setRuntime] = useState<Runtime | undefined>(undefined);
  const ready = useAppStore((state) => state.ready);
  const identity = useAppStore((state) => state.identity);
  // Экран переезда (этап 27). Куда переехали — известно до запуска среды: по
  // памяти устройства, а затем по ответу сервера.
  const moved = useMovedTarget();
  // Вход на старом адресе нужен экрану переезда, только чтобы досылать
  // очередь. Приход со ссылки из письма (`?login=code`) — тот же случай.
  const [signingIn, setSigningIn] = useState(() =>
    new URLSearchParams(globalThis.location?.search ?? '').has('login'),
  );

  useEffect(() => {
    let live = true;

    // Среда выполнения одна на вкладку и переживает перемонтирование: движок,
    // сокет и очередь не имеют отношения к жизненному циклу компонента, а
    // остановка на размонтировании обрывала бы отправку на ровном месте.
    void getRuntime().then((value) => {
      if (live) setRuntime(value);
    });

    return () => {
      live = false;
    };
  }, []);

  // Согласие коннектора уходит на новый адрес сразу: ни копия, ни вход на
  // старом ему не нужны.
  if (moved !== undefined && globalThis.location?.pathname === '/authorize') {
    return <ForwardToMoved target={moved} />;
  }

  if (runtime === undefined || !ready) {
    return <main className="boot">Открываем…</main>;
  }

  if (moved !== undefined && !(signingIn && identity === undefined)) {
    return (
      <MovedScreen
        target={moved}
        engine={runtime.engine}
        signedIn={identity !== undefined}
        onSignIn={() => setSigningIn(true)}
      />
    );
  }

  if (identity === undefined) {
    const params = new URLSearchParams(globalThis.location?.search ?? '');
    return (
      <LoginScreen
        session={runtime.session}
        initialEmail={params.get('email') ?? ''}
        initialStage={params.get('login') === 'code' ? 'code' : 'email'}
        onSignedIn={(value) => {
          void runtime.adopt(value);
        }}
      />
    );
  }

  // Согласие для коннектора: сервер привёл сюда `GET /oauth/authorize`. Вход
  // уже пройден выше — с тем же адресом в строке, поэтому после входа человек
  // попадает прямо на вопрос, а не на спринт.
  if (globalThis.location?.pathname === '/authorize') {
    const request = parseAuthorizeRequest(globalThis.location.search);
    if (request !== undefined) {
      return (
        <AuthorizeScreen
          request={request}
          email={identity.email}
          session={runtime.session}
          onRedirect={(url) => globalThis.location.assign(url)}
        />
      );
    }
  }

  return <Shell runtime={runtime} />;
}

/**
 * Оболочка приложения: всё, что видно после входа.
 *
 * Экспортируется ради сквозной проверки в jsdom. Два раза подряд — на девятом
 * этапе и на одиннадцатом — все тесты были зелёными, а в браузере страница
 * гасла: ломался не отдельный компонент, а их сборка на настоящих данных.
 * Тест, который монтирует оболочку целиком, — единственный, который такое
 * ловит без браузера.
 */
export function Shell({ runtime }: { runtime: Runtime }): React.ReactElement {
  // Подписка идёт на карты записей, а списки считаются из них. Выборка,
  // собирающая новый массив на каждый вызов, для zustand выглядит как вечно
  // меняющееся состояние: React уходит в бесконечную перерисовку, и страница
  // гаснет — ровно это и случилось при первой проверке этапа 9 в браузере.
  const taskMap = useAppStore(selectTaskMap);
  const tagMap = useAppStore(selectTagMap);
  const checklistMap = useAppStore(selectChecklistMap);
  const linkMap = useAppStore(selectLinkMap);
  const settings = useAppStore(selectSettings);
  // Кто вошёл — нужно настройкам: у строки настроек ключ не свой, а
  // пользовательский, и подписать её нечем, кроме учётной записи.
  const identity = useAppStore((state) => state.identity);

  const tasks = useMemo(() => tasksOf(taskMap), [taskMap]);
  const tags = useMemo(() => tagsByTask(tagMap), [tagMap]);
  const tagRecords = useMemo(() => tagRecordsByTask(tagMap), [tagMap]);
  // Снятые теги наружу не показываются, но при навешивании нужны: пара
  // «задача и тег» уникальна в базе вместе с надгробиями, и вернуть снятый тег
  // можно только воскрешением его же записи.
  const allTagRecords = useMemo(() => allTagRecordsByTask(tagMap), [tagMap]);
  const checklist = useMemo(() => checklistByTask(checklistMap), [checklistMap]);
  const links = useMemo(() => linksOf(linkMap), [linkMap]);
  // Снятые связи наружу не показываются, но при заведении новой нужны: пара
  // уникальна в базе вместе с надгробиями.
  const linkRecords = useMemo(() => linkRecordsOf(linkMap), [linkMap]);
  const actions = useMemo(() => createTaskActions(runtime.engine), [runtime]);

  // Всё, что нужно строкам и панели помимо самих задач, считается разом на
  // экран: вызов на строку означал бы полный проход по задачам и связям на
  // каждую из них.
  const byId = useMemo(() => new Map(tasks.map((task) => [task.id, task])), [tasks]);
  const blocked = useMemo(() => blockedIds(tasks, links), [tasks, links]);
  const projects = useMemo(() => projectSummaries(tasks), [tasks]);
  const progress = useMemo(
    () => new Map<string, ProjectProgress>(projects.map((item) => [item.task.id, item.progress])),
    [projects],
  );
  const tagList = useMemo(() => tagCounts(tasks, tags), [tasks, tags]);

  // Длина спринта — синхронизируемая настройка, но до первого её приезда
  // работать всё равно надо: неделя по умолчанию (3.2.1). Месяц пока закрыт, и
  // сохранённый раньше «месяц» показывается неделей (см. `availability.ts`).
  const sprintType = availableSprintType(settings?.sprint_type ?? 'week');
  const now = new Date();
  const current = sprintKeyOf(now, sprintType);
  const today = todayYmd(now);

  const [period, setPeriod] = useState<Period>(current);
  const [openId, setOpenId] = useState<string | undefined>(undefined);
  const [screen, setScreen] = useState<Screen>({ kind: 'sprint' });
  const [filter, setFilter] = useState<Filter>(EMPTY_FILTER);
  const [undo, setUndo] = useState<UndoNotice | undefined>(undefined);
  const closeUndo = useCallback(() => setUndo(undefined), []);
  const [searching, setSearching] = useState(false);

  // Формат определяет устройство, а не выбор человека (3.1.1): на большом
  // экране доска, на телефоне список. Доски на телефоне нет — три колонки
  // складываются в одну, и от списка она отличалась бы только хромом карточек
  // (3.1.6); списка на большом экране нет — доска показывает те же задачи
  // разом в трёх состояниях, а плоский перечень на 1100 пикселях ширины
  // оказался лишним экраном (решение владельца, 2026-09-13).
  const narrow = useNarrowScreen();
  const mouse = useMousePointer();
  // Подсказки новичку (3.16.10). Хук здесь, до ранних возвратов экранов: что
  // подсвечено, решает текущая подсказка, а подсветка — атрибут на оболочке.
  const tips = useOnboardingTips({ identity, narrow, mouse });

  // Смена длины спринта меняет и адрес показанного периода: недельный ключ в
  // месячном режиме — это спринт, в который человек больше не попадёт кнопками.
  useEffect(() => {
    setPeriod(sprintKeyOf(new Date(), sprintType));
  }, [sprintType]);

  const visible = useMemo(() => tasksInPeriod(tasks, period), [tasks, period]);

  // Что показывает список: сам спринт или его отфильтрованная часть. Периметр
  // расширяется на все спринты только когда фильтр включён (3.12.3), иначе
  // «везде» тихо подменило бы спринт всеми задачами сразу.
  const filtering = isFiltering(filter);
  const shown = useMemo(() => {
    if (!filtering) return visible;
    return applyFilter(filter.allSprints ? tasks : visible, filter, { tags, checklist });
  }, [filtering, filter, tasks, visible, tags, checklist]);

  /** То же для соседней недели, пока её показывает листание (3.2.6). */
  function shownIn(at: Period): readonly Task[] {
    const inPeriod = tasksInPeriod(tasks, at);
    if (!filtering) return inPeriod;
    return applyFilter(filter.allSprints ? tasks : inPeriod, filter, { tags, checklist });
  }

  // Открытая задача берётся из копии по идентификатору, а не запоминается
  // объектом: иначе панель показывала бы снимок на момент открытия и не
  // замечала бы ни своих правок, ни приехавших с другого устройства.
  const open = openId === undefined ? undefined : tasks.find((task) => task.id === openId);

  // Задачу могли удалить с другого устройства, пока панель открыта. Держать
  // после этого форму, правки в которой уже никуда не поедут, нельзя.
  useEffect(() => {
    if (openId !== undefined && open === undefined) setOpenId(undefined);
  }, [openId, open]);

  // Листание недель свайпом по свободному месту (3.2.6) — только на телефоне
  // и только когда поверх ничего не открыто: у карточки свой жест вниз.
  const pageSwipe = usePageSwipe({
    enabled: narrow && open === undefined,
    target: (step) => pageTarget(period, current, step),
    onTurn: (to) => {
      setScreen({ kind: 'sprint' });
      setPeriod(to);
    },
  });

  // Проект мог быть удалён с другого устройства, пока мы в него смотрим.
  // Экран, который показывает несуществующий проект, врёт; возвращаемся к
  // спринту молча — объяснять нечего, задачи там же, где были.
  const openProject = screen.kind === 'project' ? byId.get(screen.id) : undefined;
  useEffect(() => {
    if (screen.kind === 'project' && openProject === undefined) setScreen({ kind: 'sprint' });
  }, [screen, openProject]);

  /**
   * Нажатие на тег фильтрует (3.10.3) и заодно расширяет поиск на все спринты:
   * счётчики в списке тегов глобальные, и показать одну задачу там, где в
   * счётчике пять, значило бы сделать счётчик враньём.
   */
  function pickTag(tag: string | undefined): void {
    setScreen({ kind: 'sprint' });
    setFilter((prev) => ({ ...prev, tag, allSprints: tag === undefined ? prev.allSprints : true }));
  }

  const handlers: TaskHandlers = {
    onClose: (task) => {
      void actions.setStatus(task, 'done');
    },
    onReopen: (task, to) => {
      void actions.reopen(task, to);
    },
    onShiftStatus: (task, direction) => {
      void actions.shiftStatus(task, direction);
    },
    onShiftSprint: (task, direction) => {
      void actions.shiftTaskSprint(task, direction, period === 'inbox' ? current : period);
    },
    onPlan: (task, sprint) => {
      void actions.setSprint(task, sprint);
    },
    onMove: (task, sprint) => {
      const from = task.sprint;
      void actions.setSprint(task, sprint);
      // Отмена возвращает прежний спринт той же записи: ключ порядка не
      // менялся, и задача встаёт ровно туда, где стояла.
      setUndo({
        id: Date.now(),
        text: moveTargetPhrase(sprint, current),
        undo: () => void actions.setSprint({ ...task, sprint }, from),
      });
    },
    onDelete: (task) => {
      void actions.remove(task);
      // «Удалённые» и так держат задачу 30 дней (3.18.6), но идти туда за
      // промахом — не то же, что нажать «Отменить» здесь же. Плашка заодно
      // говорит, куда задача делась: без неё было непонятно, что удалённое
      // где-то лежит (отзыв 2026-10-24). Поэтому она и после карточки.
      setUndo({
        id: Date.now(),
        text: '→ В удалённые',
        undo: () => void actions.restore(task),
      });
    },
    onOpen: (task) => setOpenId(task.id),
    onOpenProject: (projectId) => setScreen({ kind: 'project', id: projectId }),
    onPickTag: (tag) => pickTag(tag),
  };

  const extras: TaskExtras = {
    tags,
    checklist,
    today,
    shownSprint: period === 'inbox' ? current : period,
    currentSprint: current,
    byId,
    progress,
    blocked,
  };

  // Панель зовёт те же действия, что и список: «сохранить» здесь нечего, любая
  // правка — это сразу мутация (3.5.2).
  const panelActions: TaskPanelActions = {
    setTitle: (task, title) => void actions.setTitle(task, title),
    setNotes: (task, notes) => void actions.setNotes(task, notes),
    setStatus: (task, status) => void actions.setStatus(task, status),
    setDue: (task, due, dueTime) => void actions.setDue(task, due, dueTime),
    setSprint: (task, sprint) => void actions.setSprint(task, sprint),
    setRepeat: (task, repeat) => void actions.setRepeat(task, repeat),
    remove: (task) => {
      setOpenId(undefined);
      handlers.onDelete(task);
    },
    setParent: (task, parentId) => void actions.setParent(task, parentId, tasks),
    createProject: (task, name) => createRelated(name, (id) => actions.setParent(task, id, tasks)),
    createSubtask: (task, name) =>
      // Вложить надо в момент создания: снаружи есть только идентификатор
      // новой задачи, а `setParent` берёт саму запись — она появится в
      // локальной копии лишь к следующей отрисовке.
      createRelated(name, undefined, task.id),
    createLinked: (task, name, side) =>
      createRelated(name, (id) =>
        side === 'depends'
          ? actions.addLink(id, task.id, linkRecords)
          : actions.addLink(task.id, id, linkRecords),
      ),
    addLink: (blockerId, blockedId) => void actions.addLink(blockerId, blockedId, linkRecords),
    removeLink: (link) => void actions.removeLink(link),
    addTag: (taskId, raw) => void actions.addTag(taskId, raw, allTagRecords.get(taskId) ?? []),
    removeTag: (tag) => void actions.removeTag(tag),
    addChecklistItem: (taskId, text, after) => void actions.addChecklistItem(taskId, text, after),
    toggleChecklistItem: (item) => void actions.toggleChecklistItem(item),
    setChecklistText: (item, text) => void actions.setChecklistText(item, text),
    removeChecklistItem: (item) => void actions.removeChecklistItem(item),
  };

  /** Бросок на доске и в списке: одно действие, два способа его сделать. */
  function drop(task: Task, placement: Placement): void {
    void actions.place(task, placement.status, placement.order_key);
  }

  function carryOver(): void {
    if (period === 'inbox') return;
    const target = nextSprint(period);

    // Переключение на целевой спринт — часть действия, а не вежливость: иначе
    // задачи уезжают в место, которого человек не видел (3.11.5).
    void actions.carryOver(carryableTasks(visible), target).then(
      () => setPeriod(target),
      () => undefined,
    );
  }

  /** Ключ, после которого встаёт новая запись: дробный индекс, а не «максимум плюс один». */
  function lastOrderKey(): string | null {
    return [...tasks].sort(compareOrder).at(-1)?.order_key ?? null;
  }

  function create(raw: string): ReturnType<typeof actions.create> {
    return actions.create(raw, period === 'inbox' ? null : period, lastOrderKey());
  }

  /**
   * Завести задачу прямо из карточки и сразу связать её с текущей (3.8.10,
   * 3.9.2). Чем именно связать, решает вызывающий: проект, подзадача и обе
   * стороны блокировки отличаются только этим.
   *
   * Новая задача встаёт в показанный спринт, а не во Входящие: то, что
   * пропало с экрана в момент создания, ищут глазами. Проекту своего спринта
   * не нужно — его задачи живут в разных неделях (3.8.4), — но и вреда от
   * спринта у него нет.
   */
  async function createRelated(
    name: string,
    link?: (id: string) => Promise<unknown>,
    parentId?: string,
  ): Promise<CreateOutcome> {
    const created = await actions.createProject(
      name,
      period === 'inbox' ? null : period,
      lastOrderKey(),
      parentId ?? null,
    );
    // Исход возвращается наверх: без номера задача не заводится, и человек
    // должен узнать об этом от карточки, а не по отсутствию результата.
    if (created.id === undefined) return created.outcome;

    await link?.(created.id);
    return 'created';
  }

  // Панель одна на все экраны: она открывается поверх (3.5.1), и с проекта её
  // видно ровно так же, как со спринта. Собирается заранее, чтобы каждый экран
  // не повторял её разметку и не разъезжался с остальными в наборе полей.
  const panel =
    open === undefined ? null : (
      // key по задаче: черновики заголовка и описания живут в локальном
      // состоянии панели, и без пересоздания при переходе к соседней задаче
      // в полях остался бы чужой текст.
      <TaskPanel
        key={open.id}
        task={open}
        tags={tagRecords.get(open.id) ?? []}
        allTags={tagList}
        checklist={checklist.get(open.id) ?? []}
        today={today}
        currentSprint={current}
        tasks={tasks}
        byId={byId}
        links={links}
        actions={panelActions}
        onOpenTask={(task) => setOpenId(task.id)}
        onOpenProject={(projectId) => {
          // Переход к проекту закрывает панель: иначе поверх нового экрана
          // осталась бы карточка задачи, из которой в него и ушли.
          setOpenId(undefined);
          setScreen({ kind: 'project', id: projectId });
        }}
        onClose={() => setOpenId(undefined)}
      />
    );

  // Плашка отмены — на всех экранах со списком, как и панель.
  const layers = (
    <>
      {panel}
      <UndoBar notice={undo} onClose={closeUndo} />
    </>
  );

  /**
   * Поиск вызывается и убирается (3.12.1).
   *
   * «Убирается» означает и то, что фильтр снимается: строка, спрятанная вместе
   * с непустым запросом, продолжала бы резать список молча — а неполный список
   * нельзя принимать за полный (3.12.4).
   */
  function toggleSearch(): void {
    if (searching) {
      setSearching(false);
      setFilter((prev) => ({ ...prev, query: '' }));
      return;
    }
    setSearching(true);
    setScreen({ kind: 'sprint' });
  }

  // Ссылка назад называет экран, а не его тип: «Неделя 36» и «Сентябрь»
  // читаются сами, а «Спринт сент.» — это сокращение внутри подписи, ради
  // которого никто ширину не экономил.
  const backLabel = periodTitle(period);

  if (screen.kind === 'settings') {
    return (
      <main className="shell">
        <SettingsView
          session={runtime.session}
          email={identity?.email}
          sprintType={sprintType}
          onSprintType={(type) => {
            // Настройки — такая же запись, как задача: своей строки в базе у
            // неё нет до первой правки, поэтому мутация идёт с `seed`.
            if (identity !== undefined) void actions.setSprintType(identity.id, type);
          }}
          onSignedOut={() => {
            // Копия стирается здесь же: задачи выдаются по учётной записи, и
            // после выхода они не должны остаться лежать на общем устройстве
            // (3.16.1).
            void runtime.forget();
          }}
          onFlushBeforeSignOut={async () => {
            // `flush` не бросает: сеть недоступна — правки остаются в очереди,
            // и ответ «столько-то ждут» и есть повод не выходить.
            await runtime.engine.flush();
            return runtime.engine.pending();
          }}
          onBack={() => setScreen({ kind: 'sprint' })}
          backLabel={backLabel}
        />
        {layers}
        <AccountFooter
          runtime={runtime}
          onTrash={() => setScreen({ kind: 'trash' })}
          onSettings={() => setScreen({ kind: 'settings' })}
        />
      </main>
    );
  }

  if (screen.kind === 'trash') {
    return (
      <main className="shell">
        {/*
          Надгробия считаются только здесь, а не разом со всем остальным: на
          прочих экранах их не показывают, и проход по всей карте записей на
          каждую перерисовку спринта был бы работой в никуда.
        */}
        <TrashView
          tasks={trashedOf(taskMap, now)}
          now={now}
          onRestore={(task) => void actions.restore(task)}
          onPurge={(task) => void actions.purge(task)}
          onBack={() => setScreen({ kind: 'sprint' })}
          backLabel={backLabel}
        />
        {layers}
        <AccountFooter
          runtime={runtime}
          onTrash={() => setScreen({ kind: 'trash' })}
          onSettings={() => setScreen({ kind: 'settings' })}
        />
      </main>
    );
  }

  if (screen.kind === 'projects') {
    return (
      <main className="shell">
        <ProjectList
          projects={projects}
          onOpen={(id) => setScreen({ kind: 'project', id })}
          onBack={() => setScreen({ kind: 'sprint' })}
          backLabel={backLabel}
        />
        {layers}
        <AccountFooter
          runtime={runtime}
          onTrash={() => setScreen({ kind: 'trash' })}
          onSettings={() => setScreen({ kind: 'settings' })}
        />
      </main>
    );
  }

  if (screen.kind === 'project' && openProject !== undefined) {
    return (
      <main className="shell">
        <ProjectView
          project={openProject}
          nested={childrenOf(tasks, openProject.id)}
          progress={projectProgress(openProject.id, tasks)}
          extras={extras}
          handlers={handlers}
          onBack={() => setScreen({ kind: 'projects' })}
          backLabel="Все проекты"
        />
        {layers}
        <AccountFooter
          runtime={runtime}
          onTrash={() => setScreen({ kind: 'trash' })}
          onSettings={() => setScreen({ kind: 'settings' })}
        />
      </main>
    );
  }

  /**
   * Страница спринта: шапка, строка записи, задачи и подвал.
   *
   * Рисуется дважды, пока листают недели (3.2.6): текущая и соседняя рядом
   * с ней. Сосед — тот же экран, а не упрощённая картинка: после
   * перелистывания он подменяется текущей неделей в том же кадре, и любое
   * отличие было бы видно как скачок. Неделя соседа своя, всё остальное —
   * поиск, фильтр, теги — общее: оно и после перелистывания останется тем же.
   */
  function sprintPage(at: Period, atShown: readonly Task[]): React.ReactElement {
    return (
      <>
        <div className="page__body">
          <SprintHeader
            period={at}
            sprintType={sprintType}
            tasks={tasks}
            now={now}
            searching={searching}
            narrow={narrow}
            onPeriod={(next) => {
              setScreen({ kind: 'sprint' });
              setPeriod(next);
            }}
            onCarryOver={carryOver}
            onProjects={() => setScreen({ kind: 'projects' })}
            onSearch={toggleSearch}
          />

          {/*
            Быстрая запись остаётся первой на экране (3.3.1): поиск и теги встают
            под неё, а не над ней. Иначе строка, ради которой продукт и открывают,
            уезжает вниз ровно тогда, когда тегов набралось побольше.
          */}
          <QuickAdd
            onCreate={create}
            /*
              Подсказки короткие не ради лаконичности: на экране айфона прежние
              обрезались на полуслове («Можно с тего»), то есть подсказка сама
              переставала быть подсказкой. Робот, который меряет вёрстку, показал,
              что в 240 пикселей поля влезают двадцать четыре знака, не больше.
              Значок слева от поля убран (решение владельца, 2026-09-08), и поле
              стало шире на его ширину — этого хватает на «можно с #тегом»; робот
              обрезку по-прежнему ловит.
            */
            placeholder={
              at === 'inbox' ? 'На потом, можно с #тегом' : 'Новая задача, можно с #тегом'
            }
          />

          <TipCard tips={tips} />

          {searching && <SearchBar filter={filter} onChange={setFilter} onClose={toggleSearch} />}

          <TagBar tags={tagList} active={filter.tag} onPick={pickTag} />

          {/*
            Полоса стоит вплотную к списку, который она описывает. Ей нужна не
            подпись, а фраза: «в н36», «в сентябре», «во Входящих». Из подписи
            её не склеить — по-русски «в сентябрь» неверно.
          */}
          <FilterNotice
            filter={filter}
            shown={atShown.length}
            scopeIn={periodIn(at)}
            onChange={setFilter}
          />

          {atShown.length === 0 ? (
            filtering ? (
              <div className="empty">
                <p>Ничего не нашлось.</p>
                <p>Попробуйте другое слово или расширьте поиск — кнопки в полосе выше.</p>
              </div>
            ) : (
              <EmptyState period={at} />
            )
          ) : narrow ? (
            <TaskList
              tasks={atShown}
              extras={
                at === period ? extras : { ...extras, shownSprint: at === 'inbox' ? current : at }
              }
              handlers={handlers}
              onDrop={drop}
            />
          ) : (
            <Board tasks={atShown} extras={extras} handlers={handlers} onDrop={drop} />
          )}
        </div>

        <AccountFooter
          runtime={runtime}
          onTrash={() => setScreen({ kind: 'trash' })}
          onSettings={() => setScreen({ kind: 'settings' })}
        />
      </>
    );
  }

  const peek = pageSwipe.peek;

  return (
    <main ref={pageSwipe.shellRef} className="shell" data-tip={tips.current?.highlight}>
      {/*
        Всё, что едет за пальцем при листании недель (3.2.6), — весь экран.
        Снаружи только плавающие слои: карточка и плашка отмены закреплены у
        экрана, а `transform` у предка привязал бы их к странице.
      */}
      <div ref={pageSwipe.pageRef} className="page">
        {sprintPage(period, shown)}

        {peek !== undefined && (
          // `inert`: соседа видно, но он не для пальца — нажать в нём можно
          // будет, когда он станет текущей неделей.
          <div
            className="page__peek"
            style={{ left: peek.left, top: peek.top }}
            aria-hidden="true"
            inert
          >
            {sprintPage(peek.to, shownIn(peek.to))}
          </div>
        )}
      </div>

      {layers}
    </main>
  );
}

function EmptyState({ period }: { period: Period }): React.ReactElement {
  if (period === 'inbox') {
    return (
      <div className="empty">
        <p>Входящие пусты.</p>
        <p>Записывайте сюда мысли на бегу, когда некогда решать, в какую неделю их ставить.</p>
      </div>
    );
  }

  return (
    <div className="empty">
      <p>В спринте пока пусто.</p>
      <p>
        Первая задача — в строке сверху. <code>#тег</code> прямо в тексте станет тегом.
      </p>
    </div>
  );
}

/**
 * Подвал: что сейчас с сохранением и куда пойти за редким.
 *
 * Раньше здесь же жили тема, ключи и учётная запись — временно, пока не было
 * настроек. Теперь всё это на своём экране, а в подвале остался индикатор
 * (он обязан быть виден всегда: «нет кнопки Сохранить» держится на нём) и две
 * ссылки на то, что нужно изредка (принцип 2).
 */
function AccountFooter({
  runtime,
  onTrash,
  onSettings,
}: {
  runtime: Runtime;
  onTrash: () => void;
  onSettings: () => void;
}): React.ReactElement {
  const status = useAppStore((state) => state.status);
  const [rejected, setRejected] = useState<QuarantinedMutation[] | undefined>(undefined);

  // Список читается из базы по нажатию, а не держится в состоянии постоянно:
  // отвергнутые правки — редкость, и следить за ними всё время незачем.
  function openRejected(): void {
    void runtime.engine.rejected().then(setRejected, () => setRejected([]));
  }

  // Правок не стало — разбор закрывается сам: диалог, который показывает
  // пустоту после «выбросить», выглядит как несработавшая кнопка.
  useEffect(() => {
    if (status.kind !== 'rejected') setRejected(undefined);
  }, [status]);

  return (
    <footer className="account">
      <SyncStatus onRejected={openRejected} />
      <button type="button" className="link" onClick={onSettings}>
        Настройки
      </button>
      <button type="button" className="link" onClick={onTrash}>
        Удалённые
      </button>

      {rejected !== undefined && (
        <Rejected
          entries={rejected}
          onForget={() => {
            void runtime.engine.forgetRejected().then(() => setRejected(undefined));
          }}
          onClose={() => setRejected(undefined)}
        />
      )}
    </footer>
  );
}
